import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtCurrency, MEASUREMENT_INFO } from '../utils/tradePayData';
import { loadPublishedWageRatesOnce, unionScaleForPayTrade, PAY_TRADE_UNION_SCALE } from '../utils/unionWages';
import { QUIZ_QUESTIONS, scoreTrades } from '../utils/tradeQuiz';
import UnionScaleBlock from './UnionScaleBlock';
import TradeCrossLinks from './TradeCrossLinks';
import useIsPhone from '../utils/useIsPhone';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const HELPER    = 'rgba(255,255,255,0.72)';

const card = {
  backgroundColor: NAVY_MID, border: '1px solid rgba(245,166,35,0.35)', borderRadius: '12px', marginBottom: '16px',
};
const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '44px', padding: '0 16px',
  background: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px',
  fontFamily: FONT_BODY, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
};
const primaryBtn = { ...secondaryBtn, backgroundColor: AMBER, color: NAVY, border: 'none' };
const toggleBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', width: '100%', minHeight: '44px',
  padding: '0 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
  color: AMBER, fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 700, textAlign: 'left', cursor: 'pointer',
};

// Small "Beta" label beside the quiz title (14px so it stays readable on phones).
function BetaBadge() {
  const { t } = useTranslation();
  return (
    <span data-quiz-beta style={{
      display: 'inline-block', verticalAlign: 'middle', fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 700,
      lineHeight: 1.2, letterSpacing: 'normal', color: AMBER, border: `1px solid ${AMBER}`, borderRadius: '999px',
      padding: '2px 8px', whiteSpace: 'nowrap',
    }}>
      {t('resources.quiz.beta')}
    </span>
  );
}

// One collapsed-by-default section inside a result.
function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: '8px' }}>
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)} style={toggleBtn}>
        <span>{label}</span><span aria-hidden="true" style={{ flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ marginTop: '10px' }}>{children}</div>}
    </div>
  );
}

function QuizResult({ result, rank, hasUnionScale, onSelectTrade }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { row, matched } = result;
  const median = fmtCurrency(row.median_pay);
  const info = MEASUREMENT_INFO[row.measurement_type] || MEASUREMENT_INFO.percentile_10_50_90;
  const medianLabel = t((info.columns.find(c => c.key === 'median_pay') || {}).labelKey || 'resources.measurement.median');
  const reasons = matched.map(key => t(`resources.quiz.why.${key}`));
  const unionBlock = <UnionScaleBlock row={row} medianLabel={medianLabel} stacked compact largeText />;
  return (
    <article data-quiz-result={row.trade} style={{
      backgroundColor: NAVY, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px',
      display: 'flex', flexDirection: 'column', minWidth: 0,
    }}>
      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: AMBER, marginBottom: '2px' }}>{t('resources.quiz.rank', { n: rank })}</p>
      <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: WHITE, lineHeight: 1.3, marginBottom: '4px', overflowWrap: 'anywhere' }}>{row.trade}</h4>
      {median && <p style={{ fontSize: '0.95rem', color: WHITE, marginBottom: '8px' }}>{t('resources.quiz.median', { pay: median })}</p>}
      <p style={{ fontSize: '0.875rem', color: HELPER, lineHeight: 1.5, marginBottom: '12px' }}>
        {t('resources.quiz.whyFits', { reasons: reasons.join(' · ') })}
      </p>
      {/* Phones keep results short: the union scale block sits in a collapsed section there. */}
      {hasUnionScale && (isPhone
        ? <Collapsible label={t('resources.unionScale.title')}>{unionBlock}</Collapsible>
        : unionBlock)}
      <Collapsible label={t('resources.quiz.schoolsUnions')}>
        <TradeCrossLinks trade={row.trade} categories={row.resource_directory_categories} />
      </Collapsible>
      <button type="button" onClick={() => onSelectTrade(row.trade)} style={{ ...primaryBtn, width: '100%', marginTop: '12px' }}>
        {t('resources.quiz.seeDetails')}
      </button>
    </article>
  );
}

// "Which trade fits you" quiz at the top of the Career Pay Guide. A banner until opened, then
// one question per screen in the same spot, then the top 3 trades for the answers. Answers live
// only in this component's state -- nothing is stored or sent. rowsForState: the guide's trade
// rows for the selected state (only these can be recommended). onSelectTrade: same handler as
// the Popular trades tiles (select the trade and scroll its card into view).
// initialOpen: start with the quiz open (deep link ?tab=pay&quiz=1), scrolled into view.
export default function TradeQuiz({ rowsForState, onSelectTrade, onBrowseAll, initialOpen = false }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(initialOpen);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [unionTrades, setUnionTrades] = useState(() => new Set());
  const rootRef = useRef(null);
  const headingRef = useRef(null);
  const total = QUIZ_QUESTIONS.length;
  const done = step >= total;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    loadPublishedWageRatesOnce().then(res => {
      if (cancelled || res.error) return;
      setUnionTrades(new Set(rowsForState.map(r => r.trade).filter(name => PAY_TRADE_UNION_SCALE[name] && unionScaleForPayTrade(res.rows, name))));
    });
    return () => { cancelled = true; };
  }, [open, rowsForState]);

  // Each new screen: move focus to its heading, and bring the top of the quiz back into view if
  // the previous screen left it scrolled off (the sticky site header is 60px).
  useEffect(() => {
    if (!open) return;
    headingRef.current?.focus({ preventScroll: true });
    const top = rootRef.current?.getBoundingClientRect().top;
    if (top != null && top < 70) window.scrollBy({ top: top - 76 });
  }, [open, step]);

  useEffect(() => {
    if (!initialOpen) return;
    requestAnimationFrame(() => rootRef.current?.scrollIntoView({ block: 'start' }));
  }, []); // eslint-disable-line

  const close = () => { setOpen(false); setStep(0); setAnswers({}); };
  const retake = () => { setStep(0); setAnswers({}); };
  const answer = (id, value) => { setAnswers(prev => ({ ...prev, [id]: value })); setStep(s => s + 1); };

  if (!open) {
    return (
      <section data-quiz-banner style={{
        ...card, padding: isPhone ? '16px' : '16px 20px',
        display: 'flex', flexDirection: isPhone ? 'column' : 'row', alignItems: isPhone ? 'stretch' : 'center', gap: '12px 20px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.35rem', fontWeight: 400, color: WHITE, letterSpacing: '0.03em', lineHeight: 1.2, marginBottom: '4px' }}>
            {t('resources.quiz.bannerTitle')} <BetaBadge />
          </h2>
          <p style={{ fontSize: '0.875rem', color: HELPER, lineHeight: 1.5 }}>{t('resources.quiz.bannerBody')}</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} style={{ ...primaryBtn, flexShrink: 0, ...(isPhone ? { width: '100%', fontSize: '1rem' } : {}) }}>
          {t('resources.quiz.start')}
        </button>
      </section>
    );
  }

  const question = QUIZ_QUESTIONS[step];
  const { results, qualified } = done ? scoreTrades(rowsForState, answers, unionTrades) : { results: [], qualified: 0 };
  const shown = Math.min(step + 1, total);

  return (
    <section ref={rootRef} data-quiz data-quiz-step={done ? 'results' : step + 1} aria-label={t('resources.quiz.bannerTitle')}
      style={{ ...card, padding: isPhone ? '16px' : '20px', scrollMarginTop: '76px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 10px', minWidth: 0 }}>
          <BetaBadge />
          {!done && (
            <p style={{ fontSize: '0.875rem', fontWeight: 700, color: HELPER }}>{t('resources.quiz.progress', { current: shown, total })}</p>
          )}
        </div>
        <button type="button" onClick={close} style={{ ...secondaryBtn, padding: '0 14px' }}>{t('resources.quiz.close')}</button>
      </div>
      <div role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done ? total : step}
        aria-label={t('resources.quiz.progress', { current: shown, total })}
        style={{ height: '6px', borderRadius: '3px', backgroundColor: 'rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ width: `${((done ? total : step) / total) * 100}%`, height: '100%', backgroundColor: AMBER, transition: 'width 0.2s' }} />
      </div>

      {!done ? (
        <>
          <h3 ref={headingRef} tabIndex={-1} style={{ fontSize: isPhone ? '1.2rem' : '1.3rem', fontWeight: 700, color: WHITE, lineHeight: 1.3, marginBottom: '14px', outline: 'none' }}>
            {t(`resources.quiz.q.${question.id}.title`)}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isPhone ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
            {question.options.map(value => {
              const chosen = answers[question.id] === value;
              return (
                <button key={value} type="button" data-quiz-option={value} aria-pressed={chosen} onClick={() => answer(question.id, value)} style={{
                  minHeight: '56px', padding: '10px 16px', fontSize: '16px', fontWeight: 600, fontFamily: FONT_BODY, lineHeight: 1.3,
                  textAlign: 'left', cursor: 'pointer', borderRadius: '10px', minWidth: 0, overflowWrap: 'anywhere',
                  backgroundColor: chosen ? 'rgba(245,166,35,0.14)' : NAVY, color: chosen ? AMBER : WHITE,
                  border: `2px solid ${chosen ? AMBER : 'rgba(255,255,255,0.14)'}`,
                }}>
                  {t(`resources.quiz.q.${question.id}.${value}`)}
                </button>
              );
            })}
          </div>
          {step > 0 && (
            <button type="button" onClick={() => setStep(s => s - 1)} style={{ ...secondaryBtn, marginTop: '14px' }}>{t('resources.quiz.back')}</button>
          )}
        </>
      ) : (
        <>
          <h3 ref={headingRef} tabIndex={-1} style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', fontWeight: 400, color: WHITE, letterSpacing: '0.03em', marginBottom: '12px', outline: 'none' }}>
            {t('resources.quiz.resultsTitle')}
          </h3>
          {/* Fewer than 3 trades matched at least 3 answers: say so, and with none, offer the full list. */}
          {qualified < 3 && (
            <p data-quiz-few role="note" style={{
              fontSize: '0.95rem', color: WHITE, lineHeight: 1.5, marginBottom: '12px', padding: '12px 14px', borderRadius: '8px',
              backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)',
            }}>
              {t('resources.quiz.fewMatches', { count: qualified })}
            </p>
          )}
          {qualified === 0 ? (
            <button type="button" data-quiz-browse onClick={() => { close(); onBrowseAll?.(); }} style={{ ...primaryBtn, ...(isPhone ? { width: '100%' } : {}) }}>
              {t('resources.quiz.browseAll')}
            </button>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isPhone ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', alignItems: 'start' }}>
              {results.map((r, i) => (
                <QuizResult key={r.row.trade} result={r} rank={i + 1}
                  hasUnionScale={unionTrades.has(r.row.trade)} onSelectTrade={onSelectTrade} />
              ))}
            </div>
          )}
          <p style={{ fontSize: '0.875rem', color: HELPER, lineHeight: 1.5, margin: '14px 0 12px' }}>{t('resources.quiz.disclaimer')}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <button type="button" onClick={() => setStep(s => s - 1)} style={secondaryBtn}>{t('resources.quiz.back')}</button>
            <button type="button" onClick={retake} style={secondaryBtn}>{t('resources.quiz.retake')}</button>
            <button type="button" onClick={close} style={secondaryBtn}>{t('resources.quiz.close')}</button>
          </div>
        </>
      )}
    </section>
  );
}
